import { useContext } from 'react'
import { DataContext } from '../../../context/DataContext'

import {
  finishCardSubscriptionRequest,
  createOneClickPaymentOrderRequest,
  createGuestPaymentOrderRequest,
  subscribeCardRequest
} from '../../../helpers/pinflagRequest'
import {
  parseCustomerData,
  parsePaymentData,
  parseSubscriptionData
} from '../../../helpers/payment/paymentOrderParsers'
import { usePayment } from '../usePayment/usePayment'
import { GETNET } from '../../../constants/payment'
import { usePaymentOrderHandlers } from '../usePaymentOrderHandlers'

export const useGetnet = () => {
  const {
    installments,
    setPaymentError,
    setIsPaying,
    selectedPaymentCard,
    selectedPaymentOption,
    forceNewPayment,
    setForceNewPayment,
    setHasConfirmedPaymentMethod,
    userToken
  } = usePayment()

  const {
    handlePaymentOrderRejected,
    handlePaymentOrderApproved,
    pollPaymentOrderStatus
  } = usePaymentOrderHandlers()

  const {
    userInfo,
    addressInfo,
    cartData,
    ecommerceInfo,
    clientMetaData,
    setShowFinalLoader
  } = useContext(DataContext)

  const data = {
    userInfo,
    addressInfo,
    cartData,
    ecommerceInfo,
    selectedPaymentOption,
    installments,
    selectedPaymentCard
  }

  const getnetExtraDataParse = (data) => {
    return {
      userAgent: data.clientMetaData.userAgent,
      ipAddress: data.clientMetaData.ipAddress
    }
  }

  const initWindow = (url, responseHandler) => {
    window.P.init(url)
    window.P.on('response', responseHandler)
  }

  const finishCardSubscription = () => {
    return finishCardSubscriptionRequest({
      paymentGateway: GETNET,
      referenceId: cartData.referenceId,
      customerData: parseCustomerData(userInfo)
    })
  }

  const setUserTokenPostSubscription = (newUserToken) => {
    userToken.current = newUserToken
  }

  const createPaymentOrderPostSubscription = async (subscribeCardResponse) => {
    const subscriptionData = {
      ...data,
      selectedPaymentCard: subscribeCardResponse.cardData
    }

    setUserTokenPostSubscription(subscribeCardResponse.userToken)

    const getnetPaymentData = parsePaymentData({
      data: subscriptionData,
      gateway: GETNET,
      useOneClick: true,
      forceNewPayment
    })
    const paymentOrderResponse =
      await createOneClickPaymentOrderRequest(getnetPaymentData, userToken.current)
    if (paymentOrderResponse.status === 'rejected') {
      handlePaymentOrderRejected({
        message: paymentOrderResponse.gatewayData.status.message
      })
    } else if (paymentOrderResponse.status === 'approved') {
      return handlePaymentOrderApproved({
        result: paymentOrderResponse,
        gateway: GETNET,
        paymentMethod: getnetPaymentData?.cardData?.cardType?.toLowerCase()
      })
    } else {
      const paymentOrderId = paymentOrderResponse.orderId
      pollPaymentOrderStatus({ orderId: paymentOrderId, gateway: GETNET, paymentMethod: getnetPaymentData?.cardData?.cardType?.toLowerCase() })
    }
  }

  // responseGetnet: Response from Getnet popup
  const subscriptionResponseHandler = async (responseGetnet) => {
    const status = responseGetnet.status.status
    console.log('Status', status)
    if (status === 'APPROVED') {
      setShowFinalLoader(true)
      const finishCardSubscriptionResponse = await finishCardSubscription()
      await createPaymentOrderPostSubscription(finishCardSubscriptionResponse)
    } else {
      setForceNewPayment(true)
      setPaymentError(
        responseGetnet.status.message || 'Error en la suscripción de la tarjeta'
      )
      setShowFinalLoader(false)
      setIsPaying(false)
      setHasConfirmedPaymentMethod(false)
    }
  }

  const subscribeCardAndPayGetnet = async () => {
    const subscriptionData = parseSubscriptionData(data, GETNET)
    const extraData = getnetExtraDataParse({ clientMetaData })
    const subscriptionDataWithExtraData = { ...subscriptionData, ...extraData }
    try {
      const subscriptionResponse = await subscribeCardRequest(
        subscriptionDataWithExtraData
      )
      initWindow(
        subscriptionResponse.redirectionUrl,
        subscriptionResponseHandler
      )
    } catch (error) {
      setPaymentError('Ocurrió un error al suscribir la tarjeta')
      setIsPaying(false)
      setHasConfirmedPaymentMethod(false)
    }
  }

  const paymentResponseHandler = async (responseGetnet) => {
    const status = responseGetnet.status.status
    if (status === 'APPROVED') {
      setShowFinalLoader(true)
      pollPaymentOrderStatus({
        orderId: responseGetnet.requestId,
        gateway: GETNET,
        paymentMethod: selectedPaymentCard?.cardType
          ? selectedPaymentCard?.cardType?.toLowerCase()
          : selectedPaymentOption
      })
    } else {
      setIsPaying(false)
      setPaymentError(responseGetnet.status.message || 'Error en el pago')
      setHasConfirmedPaymentMethod(false)
      console.error('Error al suscribir la tarjeta:', responseGetnet)
    }
  }

  const payWithGetnet = async () => {
    const paymentData = parsePaymentData({
      data,
      gateway: GETNET,
      useOneClick: false,
      forceNewPayment
    })
    const extraData = getnetExtraDataParse({ clientMetaData })
    const paymentDataWithExtraData = { ...paymentData, ...extraData }
    try {
      const paymentOrderResponse = await createGuestPaymentOrderRequest(
        paymentDataWithExtraData
      )
      const { gatewayData } = paymentOrderResponse
      initWindow(gatewayData.processUrl, paymentResponseHandler)
    } catch (error) {
      console.error('❗Error al crear la orden de pago:', error)
      setPaymentError('Ocurrió un error al crear la orden de pago')
      setIsPaying(false)
      setHasConfirmedPaymentMethod(false)
    }
  }

  const payOneClickWithGetnet = async () => {
    const paymentData = parsePaymentData({
      data,
      gateway: GETNET,
      useOneClick: true,
      forceNewPayment
    })
    const extraData = getnetExtraDataParse({ clientMetaData })
    const paymentDataWithExtraData = { ...paymentData, ...extraData }
    try {
      const paymentOrderResponse = await createOneClickPaymentOrderRequest(
        paymentDataWithExtraData, userToken.current
      )
      const { gatewayData } = paymentOrderResponse
      paymentResponseHandler(gatewayData)
    } catch (error) {
      setPaymentError('Ocurrió un error al crear la orden de pago')
      setIsPaying(false)
      setHasConfirmedPaymentMethod(false)
    }
  }

  const canUserPayWithGetnet = () => {
    return true
  }

  return {
    subscribeCardAndPayGetnet,
    payWithGetnet,
    payOneClickWithGetnet,
    canUserPayWithGetnet
  }
}
